import { Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

export interface TrackingData {
  segment: 'silver' | 'young' | 'unknown';
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmContent: string;
  utmTerm: string;
  pageUrl: string;
  referrer: string;
  deviceType: 'mobile' | 'tablet' | 'desktop';
  userAgent: string;
  submittedAt: string;
}

const UTM_STORAGE_KEY = 'mv_utm';
const SEGMENT_COOKIE = 'mv_segment';

@Injectable({ providedIn: 'root' })
export class TrackingService {
  private doc = inject(DOCUMENT);

  constructor() {
    this.captureUtmParams();
  }

  private captureUtmParams(): void {
    const win = this.doc.defaultView;
    if (!win) return;

    const params = new URLSearchParams(win.location.search);
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
    const hasUtm = utmKeys.some((key) => params.has(key));

    if (hasUtm) {
      const utm: Record<string, string> = {};
      for (const key of utmKeys) {
        utm[key] = params.get(key) || '';
      }
      win.sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utm));
    }

    // Set segment from utm_campaign or existing cookie
    const campaign = params.get('utm_campaign') || '';
    if (campaign === 'silver' || campaign === 'young') {
      this.setSegmentCookie(campaign);
    }
  }

  private setSegmentCookie(segment: string): void {
    this.doc.cookie = `${SEGMENT_COOKIE}=${segment};path=/;max-age=${60 * 60 * 24 * 90};SameSite=Lax`;
  }

  private getSegmentFromCookie(): 'silver' | 'young' | 'unknown' {
    const match = this.doc.cookie.match(new RegExp(`(?:^|;\\s*)${SEGMENT_COOKIE}=([^;]*)`));
    const value = match ? match[1] : '';
    if (value === 'silver' || value === 'young') return value;
    return 'unknown';
  }

  private getStoredUtm(): Record<string, string> {
    const win = this.doc.defaultView;
    if (!win) return {};
    try {
      return JSON.parse(win.sessionStorage.getItem(UTM_STORAGE_KEY) || '{}');
    } catch {
      return {};
    }
  }

  private detectDeviceType(): 'mobile' | 'tablet' | 'desktop' {
    const win = this.doc.defaultView;
    if (!win) return 'desktop';
    const width = win.innerWidth;
    if (width < 480) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }

  getTrackingData(): TrackingData {
    const win = this.doc.defaultView;
    const utm = this.getStoredUtm();

    return {
      segment: this.getSegmentFromCookie(),
      utmSource: utm['utm_source'] || '',
      utmMedium: utm['utm_medium'] || '',
      utmCampaign: utm['utm_campaign'] || '',
      utmContent: utm['utm_content'] || '',
      utmTerm: utm['utm_term'] || '',
      pageUrl: win?.location.href || '',
      referrer: this.doc.referrer || '',
      deviceType: this.detectDeviceType(),
      userAgent: win?.navigator.userAgent || '',
      submittedAt: new Date().toISOString(),
    };
  }
}
